/**
 * Zestina cart — Optimistic UI + debounced background sync.
 * Rule: DOM is authoritative during the session; server is synced in background.
 * Full section refetch only on drawer open (to reconcile between sessions).
 */
(function () {
  if (window.__zCartInit) return;
  window.__zCartInit = true;

  const DRAWER_SECTION_ID = 'zestina-cart-drawer';
  const DEBOUNCE_MS = 350;
  const DEBUG = true;

  function log(...a) { if (DEBUG) console.log('[z-cart]', ...a); }
  function warn(...a) { console.warn('[z-cart]', ...a); }

  const root = document.documentElement;

  // --- DOM helpers ---
  function findDrawerHost() {
    let host = document.getElementById('shopify-section-' + DRAWER_SECTION_ID);
    if (host) return host;
    for (const el of document.querySelectorAll('.shopify-section')) {
      if (el.id && el.id.endsWith('__' + DRAWER_SECTION_ID)) return el;
    }
    const d = document.querySelector('[data-z-drawer]');
    return d ? (d.closest('.shopify-section') || d.parentElement) : null;
  }
  const body = () => document.querySelector('.z-drawer__body');
  const foot = () => document.querySelector('.z-drawer__foot');
  const subtotalEl = () => document.querySelector('[data-z-drawer-total]');

  function openDrawer() {
    root.classList.add('z-drawer-open');
    const d = document.querySelector('[data-z-drawer]');
    if (d) d.setAttribute('aria-hidden', 'false');
  }
  function closeDrawer() {
    root.classList.remove('z-drawer-open');
    const d = document.querySelector('[data-z-drawer]');
    if (d) d.setAttribute('aria-hidden', 'true');
  }

  // --- money formatting: cents → "1.234,56 RSD" ---
  function fmt(cents) {
    const n = (cents | 0) / 100;
    return n.toLocaleString('sr-RS', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' RSD';
  }

  function updateSubtotalFromDom() {
    let total = 0;
    document.querySelectorAll('.z-line').forEach((line) => {
      const unit = parseInt(line.dataset.unitPrice, 10) || 0;
      const qty = parseInt(line.querySelector('[data-qty]')?.textContent, 10) || 0;
      total += unit * qty;
    });
    const el = subtotalEl();
    if (el) {
      el.textContent = fmt(total);
      el.dataset.totalCents = String(total);
    }
    document.querySelectorAll('[data-z-cart-total]').forEach((e) => { e.textContent = fmt(total); });
    updateDeliveryUi(total);
    return total;
  }

  function updateSubtotalFromServer(cents) {
    if (typeof cents !== 'number') return;
    const el = subtotalEl();
    if (el) {
      el.textContent = fmt(cents);
      el.dataset.totalCents = String(cents);
    }
    document.querySelectorAll('[data-z-cart-total]').forEach((e) => { e.textContent = fmt(cents); });
    updateDeliveryUi(cents);
  }

  // Cache free-shipping threshold + delivery fee once (survives DOM swaps; read from the liquid).
  let CACHED_FREE_SHIP_CENTS = 0;
  let CACHED_DELIVERY_FEE_CENTS = 0;

  function readDeliveryConfig() {
    const drawer = document.querySelector('[data-z-drawer]');
    if (!drawer) return;
    if (!CACHED_FREE_SHIP_CENTS) {
      const t = parseInt(drawer.dataset.freeShipCents, 10) || 0;
      if (t > 0) CACHED_FREE_SHIP_CENTS = t;
    }
    if (!CACHED_DELIVERY_FEE_CENTS) {
      const f = parseInt(drawer.dataset.deliveryFeeCents, 10) || 0;
      if (f > 0) CACHED_DELIVERY_FEE_CENTS = f;
    }
  }

  // Free-shipping progress + delivery preview. Checkout is NEVER gated — any amount can check out.
  // The actual delivery charge is applied by Shopify at checkout via shipping rates; this is a preview.
  function updateDeliveryUi(currentCents) {
    readDeliveryConfig();
    const threshold = CACHED_FREE_SHIP_CENTS;
    const fee = CACHED_DELIVERY_FEE_CENTS;
    if (threshold <= 0) return;

    const reached = currentCents >= threshold;
    const pct = Math.min(100, Math.max(0, (currentCents / threshold) * 100));

    // CSS variable on <html> — survives any innerHTML swap of the section host.
    document.documentElement.style.setProperty('--z-progress-pct', String(pct));
    document.documentElement.setAttribute('data-z-free-ship-reached', reached ? 'true' : 'false');

    const progress = document.querySelector('[data-z-progress]');
    const msg = document.querySelector('[data-z-progress-msg]');

    if (progress) progress.setAttribute('data-reached', reached ? 'true' : 'false');
    if (msg) {
      msg.setAttribute('data-reached', reached ? 'true' : 'false');
      if (reached) {
        msg.innerHTML = 'Ostvarili ste besplatnu dostavu ✓';
      } else {
        const missing = Math.max(0, threshold - currentCents);
        msg.innerHTML = `Još <strong>${fmt(missing)}</strong> do besplatne dostave`;
      }
    }

    // Delivery line + grand total preview
    const deliveryCents = reached ? 0 : fee;
    const deliveryEl = document.querySelector('[data-z-delivery-val]');
    if (deliveryEl) deliveryEl.textContent = reached ? 'Besplatno' : fmt(fee);
    const grandEl = document.querySelector('[data-z-grand-total]');
    if (grandEl) grandEl.textContent = fmt(currentCents + deliveryCents);
  }

  function showEmptyState() {
    const b = body();
    if (b) b.innerHTML = '<p class="z-drawer__empty">Korpa je prazna.</p>';
    const f = foot();
    if (f) f.setAttribute('hidden', '');
    const pw = document.querySelector('[data-z-progress-wrap]');
    if (pw) pw.setAttribute('hidden', '');
    const subEl = subtotalEl();
    if (subEl) {
      subEl.textContent = fmt(0);
      subEl.dataset.totalCents = '0';
    }
    document.querySelectorAll('[data-z-cart-total]').forEach((e) => { e.textContent = fmt(0); });
    updateDeliveryUi(0);
  }

  function swapDrawer(newInnerHtml) {
    const host = findDrawerHost();
    if (!host) return false;
    host.innerHTML = newInnerHtml;
    const el = subtotalEl();
    if (el) {
      document.querySelectorAll('[data-z-cart-total]').forEach((e) => { e.textContent = el.textContent; });
      const cents = parseInt(el.dataset.totalCents, 10) || 0;
      updateDeliveryUi(cents);
    }
    return true;
  }

  async function refetchSection() {
    try {
      const res = await fetch(`/?section_id=${DRAWER_SECTION_ID}&_=${Date.now()}`, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { 'Accept': 'text/html' },
      });
      if (!res.ok) return;
      const text = await res.text();
      swapDrawer(text);
    } catch (e) { /* ignore */ }
  }

  // --- Background server sync (no section swap — DOM is authoritative) ---
  let inFlightCount = 0;
  function hasPendingWork() { return inFlightCount > 0 || pending.size > 0 || batchTimer !== null; }

  // Serialize requests — avoids Shopify rate-limit (429) when mutating multiple lines rapidly.
  let requestChain = Promise.resolve();

  function serverMutate(url, fd) {
    inFlightCount++;
    const run = requestChain.then(async () => {
      try {
        const res = await fetch(url, {
          method: 'POST',
          body: fd,
          headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          credentials: 'same-origin',
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          // Retry once on 429 with small delay
          if (res.status === 429) {
            await new Promise((r) => setTimeout(r, 600));
            return serverMutate(url, fd);
          }
          throw Object.assign(new Error('cart api'), { status: res.status, data });
        }
        return data;
      } finally {
        inFlightCount--;
      }
    });
    // Keep chain alive even on error
    requestChain = run.catch(() => {});
    return run;
  }

  // Batched pending changes: map of variantId → targetQty.
  // All changes within a debounce window flush in a single /cart/update.js request.
  const pending = new Map();
  let batchTimer = null;

  function queueServerChange(variantId, targetQty) {
    pending.set(variantId, targetQty);
    if (batchTimer) clearTimeout(batchTimer);
    batchTimer = setTimeout(flushBatch, DEBOUNCE_MS);
  }

  async function flushBatch() {
    batchTimer = null;
    if (pending.size === 0) return;
    const snapshot = new Map(pending);
    pending.clear();

    // If all updates set qty = 0, use /cart/clear.js (faster + cleaner).
    const allZero = [...snapshot.values()].every((q) => q === 0);

    try {
      if (allZero && snapshot.size >= 2) {
        await serverMutate('/cart/clear.js', new FormData());
        log('batch cleared', snapshot.size, 'items');
      } else {
        const fd = new FormData();
        snapshot.forEach((qty, vid) => {
          fd.append(`updates[${vid}]`, String(qty));
        });
        await serverMutate('/cart/update.js', fd);
        log('batch synced', snapshot.size, 'items');
      }
    } catch (err) {
      warn('batch sync failed', err);
      refetchSection();
    }
  }

  // --- Optimistic actions ---
  function qtyFromLine(lineEl) {
    return parseInt(lineEl.querySelector('[data-qty]')?.textContent, 10) || 0;
  }
  function updateLineDom(lineEl, newQty) {
    const qtyEl = lineEl.querySelector('[data-qty]');
    const totalEl = lineEl.querySelector('[data-line-total]');
    const unit = parseInt(lineEl.dataset.unitPrice, 10) || 0;
    qtyEl.textContent = String(newQty);
    if (totalEl) totalEl.textContent = fmt(unit * newQty);
  }

  function optimisticQtyChange(lineEl, newQty) {
    const variantId = lineEl.dataset.variantId;
    if (newQty <= 0) {
      lineEl.remove();
      if (!document.querySelector('.z-line')) showEmptyState();
      else updateSubtotalFromDom();
      if (variantId) queueServerChange(variantId, 0);
      return;
    }
    updateLineDom(lineEl, newQty);
    updateSubtotalFromDom();
    if (variantId) queueServerChange(variantId, newQty);
  }

  async function optimisticAdd(form) {
    const variantId = form.dataset.variantId;
    const title = form.dataset.productTitle;
    const url = form.dataset.productUrl;
    const price = parseInt(form.dataset.productPrice, 10) || 0;
    const image = form.dataset.productImage;
    const quantity = parseInt(form.querySelector('input[name="quantity"]')?.value, 10) || 1;
    if (!variantId) return;

    // Make sure the empty placeholder is gone and footer is visible
    const b = body();
    if (b) {
      const empty = b.querySelector('.z-drawer__empty');
      if (empty) empty.remove();
    }
    const f = foot();
    if (f) f.removeAttribute('hidden');
    const pw = document.querySelector('[data-z-progress-wrap]');
    if (pw) pw.removeAttribute('hidden');

    // If line exists, just bump quantity and sync
    const existing = document.querySelector(`.z-line[data-variant-id="${variantId}"]`);
    if (existing) {
      const cur = qtyFromLine(existing);
      updateLineDom(existing, cur + quantity);
      updateSubtotalFromDom();
      openDrawer();
      queueServerChange(variantId, cur + quantity);
      return;
    }

    // Create new optimistic line
    const line = document.createElement('div');
    line.className = 'z-line';
    line.dataset.variantId = variantId;
    line.dataset.unitPrice = String(price);
    line.dataset.pending = 'true';
    line.innerHTML = `
      <a class="z-line__img" href="${url || '#'}">${image ? `<img src="${image}" alt="${title}" loading="lazy" width="72" height="72">` : ''}</a>
      <div class="z-line__info">
        <h3 class="z-line__title">${title}</h3>
        <p class="z-line__price">${fmt(price)} / kom</p>
        <div class="z-line__qty">
          <button type="button" class="z-line__qty-btn" data-z-change-qty data-delta="-1" aria-label="Smanji količinu">−</button>
          <span class="z-line__qty-val" data-qty>${quantity}</span>
          <button type="button" class="z-line__qty-btn" data-z-change-qty data-delta="1" aria-label="Povećaj količinu">+</button>
        </div>
      </div>
      <div class="z-line__right">
        <span class="z-line__total" data-line-total>${fmt(price * quantity)}</span>
        <button type="button" class="z-line__remove" data-z-change-qty data-quantity="0" aria-label="Ukloni">
          <svg viewBox="0 0 24 24" fill="none"><path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2M6 6l1 14a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </button>
      </div>
    `;
    if (b) b.prepend(line);
    updateSubtotalFromDom();
    openDrawer();

    // Fire server add in background — DOM is authoritative, no refetch needed.
    const fd = new FormData(form);
    try {
      await serverMutate('/cart/add.js', fd);
    } catch (err) {
      warn('add failed — rolling back', err);
      line.remove();
      if (!document.querySelector('.z-line')) showEmptyState();
      else updateSubtotalFromDom();
    }
  }

  // --- handlers ---
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('[data-z-cart-form]');
    if (form) {
      e.preventDefault();
      const btn = form.querySelector('.z-card__add');
      const span = btn?.querySelector('span');
      const original = span?.textContent;
      if (btn) { btn.dataset.zSuccess = 'true'; if (span) span.textContent = 'Dodato ✓'; }
      optimisticAdd(form).finally(() => {
        setTimeout(() => {
          if (btn) { btn.dataset.zSuccess = 'false'; if (span && original) span.textContent = original; }
        }, 1500);
      });
      return;
    }
    const dForm = e.target.closest('[data-z-discount-form]');
    if (dForm) {
      e.preventDefault();
      const code = (dForm.querySelector('input[name="discount"]')?.value || '').trim();
      const fd = new FormData();
      fd.append('discount', code);
      fd.append('sections', DRAWER_SECTION_ID);
      serverMutate('/cart/update.js', fd).then((data) => {
        if (data.sections) {
          const keys = Object.keys(data.sections);
          const k = keys.find((x) => x === DRAWER_SECTION_ID) || keys[0];
          if (k && data.sections[k]) swapDrawer(data.sections[k]);
        }
      }).catch(() => refetchSection());
    }
  });

  // --- Cart note (napomena): persist to cart.note on blur, and flush before checkout ---
  let noteSave = Promise.resolve();
  let noteSaving = false;
  function flushNote(noteEl) {
    const fd = new FormData();
    fd.append('note', noteEl.value);
    noteEl.dataset.zSaved = noteEl.value;
    noteSaving = true;
    noteSave = serverMutate('/cart/update.js', fd).catch(() => {}).finally(() => { noteSaving = false; });
    return noteSave;
  }
  document.addEventListener('change', (e) => {
    const noteEl = e.target.closest('[data-z-cart-note]');
    if (noteEl && noteEl.value !== (noteEl.dataset.zSaved || '')) flushNote(noteEl);
  });
  document.addEventListener('click', (e) => {
    const checkoutBtn = e.target.closest('[data-z-checkout-btn]');
    if (!checkoutBtn) return;
    const noteEl = document.querySelector('[data-z-cart-note]');
    if (!noteEl) return;
    const dirty = noteEl.value !== (noteEl.dataset.zSaved || '');
    if (!dirty && !noteSaving) return; // nothing pending — let the link navigate normally
    e.preventDefault();
    const href = checkoutBtn.getAttribute('href') || '/checkout';
    const pending = dirty ? flushNote(noteEl) : noteSave;
    Promise.resolve(pending).finally(() => { window.location.href = href; });
  });

  document.addEventListener('click', (e) => {
    // Product/card qty +/- (outside cart drawer)
    const qtyDec = e.target.closest('[data-z-qty-dec]');
    const qtyInc = e.target.closest('[data-z-qty-inc]');
    if (qtyDec || qtyInc) {
      const qty = (qtyDec || qtyInc).closest('[data-z-qty]');
      const input = qty?.querySelector('[data-z-qty-input]');
      if (input) {
        const current = parseInt(input.value, 10) || 1;
        input.value = qtyInc ? current + 1 : Math.max(1, current - 1);
      }
      return;
    }

    const openLink = e.target.closest('[data-z-cart-link]');
    if (openLink) {
      e.preventDefault();
      openDrawer();
      // Only reconcile from server if no pending or in-flight mutations (DOM is more current).
      if (!hasPendingWork()) refetchSection();
      return;
    }
    const closeEl = e.target.closest('[data-z-drawer-close]');
    if (closeEl) { e.preventDefault(); closeDrawer(); return; }

    const qtyBtn = e.target.closest('[data-z-change-qty]');
    if (qtyBtn) {
      e.preventDefault();
      const lineEl = qtyBtn.closest('.z-line');
      if (!lineEl) return;
      let newQty;
      if (qtyBtn.dataset.delta !== undefined) {
        newQty = Math.max(0, qtyFromLine(lineEl) + parseInt(qtyBtn.dataset.delta, 10));
      } else {
        newQty = parseInt(qtyBtn.dataset.quantity, 10);
      }
      if (!Number.isFinite(newQty)) return;
      optimisticQtyChange(lineEl, newQty);
      return;
    }

    const rm = e.target.closest('[data-z-remove-discount]');
    if (rm) {
      e.preventDefault();
      const fd = new FormData();
      fd.append('discount', '');
      fd.append('sections', DRAWER_SECTION_ID);
      serverMutate('/cart/update.js', fd).then((data) => {
        const keys = data.sections ? Object.keys(data.sections) : [];
        const k = keys.find((x) => x === DRAWER_SECTION_ID) || keys[0];
        if (k && data.sections[k]) swapDrawer(data.sections[k]);
      }).catch(() => {});
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && root.classList.contains('z-drawer-open')) closeDrawer();
  });

  function initialSync() {
    const el = subtotalEl();
    if (el) {
      document.querySelectorAll('[data-z-cart-total]').forEach((e) => { e.textContent = el.textContent; });
      const cents = parseInt(el.dataset.totalCents, 10) || 0;
      updateDeliveryUi(cents);
    }
  }
  if (document.readyState !== 'loading') initialSync();
  else document.addEventListener('DOMContentLoaded', initialSync);

  // Also run after any section swap
  const observer = new MutationObserver(() => {
    const el = subtotalEl();
    if (el) {
      const cents = parseInt(el.dataset.totalCents, 10) || 0;
      updateDeliveryUi(cents);
    }
  });
  const host = findDrawerHost();
  if (host) observer.observe(host, { childList: true, subtree: false });

  window.zCart = { openDrawer, closeDrawer };
  log('init ok (optimistic + debounced)');
})();
